import React, { useEffect, useState } from 'react';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import FormField from '../../components/common/FormField';
import { DocumentTypeDTO, MemberAdditionalDocumentDTO, MemberFirearmDTO } from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import { useFormValidation, ValidationRules } from '../../hooks/useFormValidation';
import { useDocumentTypeApi } from '../../hooks/useDocumentTypeApi';
import { FileTypeConstants } from '../../constants/FileTypeConstants';
import { useFileApi } from '../../hooks/useFileApi';
import ImageLink from '../common/ImageLink';
import FileUpload from '../common/FileUpload';
import { getMemberId } from '../../utils/tokenUtils';

interface MemberAdditionalDocumentFormProps {
  initialValues: MemberAdditionalDocumentDTO;
  onSubmit: (data: MemberAdditionalDocumentDTO) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const FORM_SECTIONS = {
  basicInfo: {
    id: 'basic-info',
    title: 'Basic Information'
  }
} as const;

const MemberAdditionalDocumentForm: React.FC<MemberAdditionalDocumentFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  isLoading = false
}) => {
  const memberId = getMemberId();
  const [formData, setFormData] = useState<MemberAdditionalDocumentDTO>(() => ({
    ...initialValues,
    memberId: memberId || initialValues.memberId
  }));
  const [documentTypes, setDocumentTypes] = useState<DocumentTypeDTO[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<{
    [key: string]: boolean;
  }>({});

  const { uploadFile, getFileUrl } = useFileApi();
  const { search: searchDocumentTypes, isLoading: isLoadingDocumentTypes } = useDocumentTypeApi();

  const validationRules: ValidationRules = {
    documentTypeId: [{required: true, message: 'Document Type is required'}],
    documentName: [{ required: true, message: 'Document Name is required' }],
    documentId: [{ required: true, message: 'Document is required' }],
  };

  const { errors, validate } = useFormValidation<MemberAdditionalDocumentDTO>(validationRules);

  useEffect(() => {
    const fetchDocumentTypeOptions = async () => {
      try {
        const response = await searchDocumentTypes({
          filters: { isActive: true },
          page: 1,
          pageSize: 999,
          sortBy: 'DisplayPosition',
        });
        setDocumentTypes(response?.data?.results || []);
      } catch (error) {
        console.error('Error fetching document types:', error);
      }
    };

    fetchDocumentTypeOptions();
  }, []);

  const handleSave = () => {
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  const handleFileChange = (field: keyof MemberAdditionalDocumentDTO) => async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        setUploadingFiles((prev) => ({ ...prev, [field]: true }));
        const response = await uploadFile(
          'files',
          file.name,
          file,
          'members'
        );
        if (response && response.success && response.data) {
          setFormData(prev => ({
            ...prev,
            [field]: response.data
          }));
        }
      } catch (error) {
        console.error('Error handling file:', error);
      } finally {
        setUploadingFiles((prev) => ({ ...prev, [field]: false }));
      }
    }
  };

  const sections = Object.values(FORM_SECTIONS);

  console.table({ formData, errors });
  return (
    <Form sections={sections} onSave={handleSave} onCancel={onCancel} isSubmitting={isLoading}>
      <FormSection
        id={FORM_SECTIONS.basicInfo.id}
        title={FORM_SECTIONS.basicInfo.title}
      >
        <FormField
          id="documentTypeId"
          label="Document Type Id"
          type="dropdown"
          value={formData.documentTypeId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Document Type Id"
          required={true}
          error={errors.documentTypeId}
          options={documentTypes.map((type) => ({ value: type.id, label: type.title }))}
          isLoading={isLoadingDocumentTypes}
        />
        <FormField
          id="documentName"
          label="Document Name"
          type="text"
          value={formData.documentName || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Document Name"
          required={true}
          error={errors.documentName}
        />
        {formData.documentId && (
          <ImageLink
            fileId={formData.documentId}
            label="Additional Document"
          />
        )}
        <FileUpload
          id="documentId"
          onChange={handleFileChange('documentId')}
          title="Upload Additional Document"
          accept={FileTypeConstants.ALL_SUPPORTED}
          required={true}
          error={errors.documentId}
          isUploading={uploadingFiles['documentId']}
        />
      </FormSection>
    </Form>
  );
};

export default MemberAdditionalDocumentForm;